/* eslint-disable no-param-reassign */
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework from '@greenville/framework';
import { statusColorUtils } from '@aet/components';
import { getSnapshot } from 'mobx-state-tree';
import CommonUtils from './CommonUtils';
import * as constants from '../constants';
import {
  INSIGHT_RISK_BADGES_QUESTION,
  INSIGHT_RISK_BADGE_DISPLAY_THRESHOLD,
  INSIGHT_RISK_BADGE_TABLE_LIMIT,
  ENGAGEMENT_INSIGHT_RISK_KEY,
  OVERALL_DIFFICULTY_RISK_MIN_DISPLAY_THRESHOLD,
  OVERALL_STUDENT_RISK_MIN_DISPLAY_THRESHOLD,
  ASSIGNMENT_STATUS_TYPES

} from '../../modules/assignableEtext/common/Constants';
import { hasAssignmentDueDatePassed } from '../../modules/assignableEtext/utils/StudentDueDatesAdjustmentUtils';

const fallbackAetInsightsThresholds = () => ({
  insightRiskBadgeDisplayThreshold: INSIGHT_RISK_BADGE_DISPLAY_THRESHOLD,
  insightRiskBadgeTableLimit: INSIGHT_RISK_BADGE_TABLE_LIMIT,
  overallDifficultyRiskMinDisplay: OVERALL_DIFFICULTY_RISK_MIN_DISPLAY_THRESHOLD,
  overallStudentRiskMinDisplay: OVERALL_STUDENT_RISK_MIN_DISPLAY_THRESHOLD
});


/**
 * @author: Mohamed yasar arabath M
 * @description: Utils for Assignments
 * AssignmentHelperUtils.js
 */

let aetInsightsThresholdResolver = null;

class AssignmentHelperUtils {
  static STATUS_COLOR_MAP = {
    completed: {
      color: statusColorUtils.getProgressColor(constants.STATUS_COLOR_MAP.COMPLETED),
      type: constants.STATUS_COLOR_MAP.COMPLETED,
      label: 'aet.home.progressCompletedOnTimeLabel'
    },
    pastDueCompleted: {
      color: statusColorUtils.getProgressColor(constants.STATUS_COLOR_MAP.COMPLETED_PAST_DUE),
      type: constants.STATUS_COLOR_MAP.COMPLETED_PAST_DUE,
      label: 'aet.home.progressCompletedPastDueLabel'
    },
    inProgress: {
      color: statusColorUtils.getProgressColor(constants.STATUS_COLOR_MAP.IN_PROGRESS),
      type: constants.STATUS_COLOR_MAP.IN_PROGRESS,
      label: 'aet.home.progressInProgressLabel'
    },
    notStarted: {
      color: statusColorUtils.getProgressColor(constants.STATUS_COLOR_MAP.NOT_STARTED),
      type: constants.STATUS_COLOR_MAP.NOT_STARTED,
      label: 'aet.home.NotAttemptedLabel'
    }
  };

  static PERFORMANCE_STATUS_COLOR_MAP = {
    totalQuestions: {
      color: '#171717',
      type: constants.PERFORMANCE_STATUS.QUESTIONS_TOTAL,
      label: 'aet.home.questionsTotalLabel'
    },
    great: {
      color: statusColorUtils.getDotColor(constants.PERFORMANCE_STATUS.GREAT),
      type: constants.PERFORMANCE_STATUS.GREAT,
      label: 'aet.home.segmentGreatLabel'
    },
    gettingThere: {
      color: statusColorUtils.getDotColor(constants.PERFORMANCE_STATUS.GETTING_THERE),
      type: constants.PERFORMANCE_STATUS.GETTING_THERE,
      label: 'aet.home.segmentGettingThereLabel'
    },
    needReview: {
      color: statusColorUtils.getDotColor(constants.PERFORMANCE_STATUS.NEEDS_REVIEW),
      type: constants.PERFORMANCE_STATUS.NEEDS_REVIEW,
      label: 'aet.home.segmentNeedsReviewLabel'
    },
    notAttmpted: {
      color: '#171717',
      type: constants.PERFORMANCE_STATUS.NOT_ATTEMPTED,
      label: 'aet.home.NotAttemptedLabel'
    }
  };

  static getAssignmentChaptersNumbers(chapters = []) {
    if (chapters.length === 0) {
      return null;
    }
    const chapterNumbers = chapters
      .map(chapter => chapter?.match(/\d+(?:\.\d+)*/)?.[0])
      .filter(val => val !== null && val !== undefined && val !== '');
    const uniqueChapterNumbers = Array.from(new Set(chapterNumbers));

    return CommonUtils.semanticSorting(uniqueChapterNumbers);
  }

  /**
   * helper to check if all assignments have due date override
   */
  static courseAssignmentsAllHaveDueDateOverride(assignments = []) {
    if (!Array.isArray(assignments) || assignments.length === 0) {
      return false;
    }
    return assignments.every(a => a?.hasOverride === true);
  }

  /**
   * helper to check if single assignment has due date override
   * will return false in case all assignments have due date override
   */
  static assignmentRowShowInlineDueDateOverrideInfo(assignment, allAssignmentsHaveOverride) {
    if (allAssignmentsHaveOverride) {
      return false;
    }
    return assignment?.hasOverride === true;
  }

  /**
   * helper to check if any assignment has due date override
   */
  static courseAssignmentsAnyHaveDueDateOverride(assignments = []) {
    if (!Array.isArray(assignments) || assignments.length === 0) return false;
    return assignments.some(a => a?.hasOverride === true);
  }

  /**
   * Calculate progress summary percentages for a student.
   * @param {Object} progressSummary - Object with progress counts.
   * @param {number} totalAssignments - Total number of assignments.
   * @returns {Object} Percentages for each progress status.
   */
  static calculateProgressPercentages(progressSummary, totalAssignments) {
    if (totalAssignments <= 0) {
      return Object.fromEntries(
        Object.entries(progressSummary).map(([key]) => [
          key,
          0
        ])
      );
    }

    return Object.fromEntries(
      Object.entries(progressSummary).map(([key, value]) => [
        key,
        Math.round((Number(value) / Number(totalAssignments)) * 100)
      ])
    );
  }

  /**
   * Reverse of {@link calculateProgressPercentages}: display summary per status (`"count (pct%)"`).
   * Same shape as {@link getProgressSummary} progressSummary object.
   * @param {Object} progressSummaryPercentages - Percentages per progress status (0–100).
   * @param {number} totalAssignments - Total number of assignments.
   * @returns {Object<string, string>}
   */
  static calculateProgressCounts(progressSummaryPercentages, totalAssignments) {
    const summary = progressSummaryPercentages || {};
    const total = Number(totalAssignments) || 0;

    return Object.fromEntries(
      Object.entries(summary).map(([key, percentage]) => {
        const pct = Number(percentage) || 0;
        const count = total <= 0 ? 0 : Math.round((pct / 100) * total);
        return [key, `${count} (${pct}%)`];
      })
    );
  }

  /**
   * Builds completion bar + tooltip segments from counts and percentages.
   * `value` is the numeric percentage for ProgressBarWidget; `displayValue` is PPCOREAH-510 tooltip text.
   * @param {Array<{ key: string, count: number, percentage: number }>} entries
   * @returns {Array<{ label: string, value: number, displayValue: string, color: string, type: string }>}
   */
  static buildCompletionProgressSegments(entries) {
    const language = Framework.getStoreRegistry().getStore('language');

    return entries.map(({ key, count, percentage }) => {
      const safeCount = Number(count) || 0;
      const safePercentage = Number(percentage) || 0;
      const { color, type, label } = this.STATUS_COLOR_MAP[key];

      return {
        label: language.getMessage(label),
        value: safePercentage,
        displayValue: `${safeCount} (${safePercentage}%)`,
        color,
        type
      };
    });
  }

  /**
   * Roster completion column segments. `progressSummary` on each student holds percentages only
   * (counts are converted in renderStudentsList), so tooltip counts are derived from
   * percentage × totalAssignments — same approach as calculateProgressCounts.
   * @param {Object} progressSummaryPercentages - Per-status percentages (0–100) after renderStudentsList.
   * @param {number} totalAssignments
   * @returns {Array<{ label: string, value: number, displayValue: string, color: string, type: string }>}
   */
  static getRosterProgressSegments(progressSummaryPercentages, totalAssignments) {
    const summary = progressSummaryPercentages || {};
    const total = Number(totalAssignments) || 0;

    return AssignmentHelperUtils.buildCompletionProgressSegments(
      Object.entries(summary).map(([key, percentage]) => {
        const safePercentage = Number(percentage) || 0;
        const count = total <= 0 ? 0 : Math.round((safePercentage / 100) * total);

        return { key, count, percentage: safePercentage };
      })
    );
  }

  static renderPerformanceProgress(performance, totalQuestions) {
    const performanceTooltip = constants.PERFORMANCE_STATUS_KEYS.map(key => ({
      label: Framework.getStoreRegistry().getStore('language').getMessage(this.PERFORMANCE_STATUS_COLOR_MAP[key].label),
      value: performance[key],
      color: statusColorUtils.getTextColor(this.PERFORMANCE_STATUS_COLOR_MAP[key].type),
      type: this.PERFORMANCE_STATUS_COLOR_MAP[key].type
    }));

    const performanceData = constants.PERFORMANCE_STATUS_KEYS.slice(1).map(key => ({
      label: Framework.getStoreRegistry().getStore('language').getMessage(this.PERFORMANCE_STATUS_COLOR_MAP[key].label),
      value: Math.round((Number(performance[key]) / Number(totalQuestions)) * 100),
      color: statusColorUtils.getProgressColor(this.PERFORMANCE_STATUS_COLOR_MAP[key].type),
      type: this.PERFORMANCE_STATUS_COLOR_MAP[key].type
    }));

    return { performanceData, performanceTooltip };
  }

  static renderProgress(progressSummary) {
    const language = Framework.getStoreRegistry().getStore('language');

    return Object.entries(progressSummary).map(([key, value]) => {
      const { color, type, label } = this.STATUS_COLOR_MAP[key];

      return {
        label: language.getMessage(label),
        value: Number(value) || 0,
        color,
        type
      };
    });
  }

  static renderStudentsList(data) {
    const renderData = data;
    const { totalAssignments = 0, students = [] } = renderData;
    renderData.students = students.map((student) => {
      const progressSummary = AssignmentHelperUtils.calculateProgressPercentages(
        student.progressSummary,
        totalAssignments
      );
      // progressSummary may use userId; roster model uses studentId
      const studentId = student.studentId || student.userId;

      return {
        ...student,
        studentId,
        id: studentId,
        name: `${student.firstName || ''} ${student.lastName || ''}`.trim(),
        progressSummary,
        progress: this.renderProgress(progressSummary),
        completionSortValue:
          (progressSummary.completed || 0)
          + (progressSummary.pastDueCompleted || 0)
          + (progressSummary.inProgress || 0),
        overallStatus: this.getOverallStatusForStudent(student)
      };
    });

    return renderData;
  }

  static normalizeOverallStatus(value) {
    if (value == null || value === '') return '';
    return String(value).trim().toLowerCase().replace(/-/g, '_');
  }

  /**
   * Roster / overall-status badge only (`id`, `color`, `label`, `status`).
   * @returns {{ id: string, color: string, label: string, status: string }|null}
   */
  static resolveOverallStatus(raw) {
    const normalizedKey = AssignmentHelperUtils.normalizeOverallStatus(raw);
    const { OVERALL_STATUS, STATUS_COLOR_MAP } = constants;
    const language = Framework.getStoreRegistry().getStore('language');

    if (!normalizedKey) {
      return null;
    }

    switch (normalizedKey) {
      case OVERALL_STATUS.EXCELLING:
        return {
          id: OVERALL_STATUS.EXCELLING,
          color: statusColorUtils.getBackgroundColor(STATUS_COLOR_MAP.COMPLETED),
          label: language.getMessage('aet.home.overallStatus.excelling'),
          status: STATUS_COLOR_MAP.COMPLETED
        };
      case OVERALL_STATUS.FALLING_BEHIND:
        return {
          id: OVERALL_STATUS.FALLING_BEHIND,
          color: statusColorUtils.getBackgroundColor(STATUS_COLOR_MAP.NOT_STARTED),
          label: language.getMessage('aet.home.overallStatus.fallingBehind'),
          status: STATUS_COLOR_MAP.NOT_STARTED
        };
      case OVERALL_STATUS.KEEPING_UP:
        return {
          id: OVERALL_STATUS.KEEPING_UP,
          color: statusColorUtils.getBackgroundColor(STATUS_COLOR_MAP.IN_PROGRESS),
          label: language.getMessage('aet.home.overallStatus.keepingUp'),
          status: STATUS_COLOR_MAP.IN_PROGRESS
        };
      default:
        return null;
    }
  }

  /**
   * Struggling-students "Comprehension" column (`AETHomePlugin`): maps API overall status → `PERFORMANCE_STATUS`
   * for `BadgeTemplate` / `getSectionNameFromStatus` — separate from roster overall-status badge data.
   */
  static overallStatusToPerformanceStatus(raw) {
    const normalizedKey = AssignmentHelperUtils.normalizeOverallStatus(raw);
    const { OVERALL_STATUS, PERFORMANCE_STATUS } = constants;
    switch (normalizedKey) {
      case OVERALL_STATUS.EXCELLING:
        return PERFORMANCE_STATUS.GREAT;
      case OVERALL_STATUS.FALLING_BEHIND:
        return PERFORMANCE_STATUS.NEEDS_REVIEW;
      case OVERALL_STATUS.KEEPING_UP:
        return PERFORMANCE_STATUS.GETTING_THERE;
      default:
        return '';
    }
  }

  static getOverallStatusForStudent(student = {}) {
    // Only called from renderStudentsList (roster / progress-summary); API status is under performanceSummary.
    return AssignmentHelperUtils.resolveOverallStatus(
      student.performanceSummary?.overallStatus
    );
  }

  static getProgressSummary(progress) {
    return AssignmentHelperUtils.buildCompletionProgressSegments([
      { key: 'completed', count: progress?.completedOnTimeCount, percentage: progress?.completedOnTimePercentage },
      { key: 'pastDueCompleted', count: progress?.completedLateCount, percentage: progress?.completedLatePercentage },
      { key: 'inProgress', count: progress?.inProgressCount, percentage: progress?.inProgressPercentage },
      { key: 'notStarted', count: progress?.notStartedCount, percentage: progress?.notStartedPercentage }
    ]);
  }

  /**
   * Get the mapping of status values to section names
   * @returns {Object} Status to section name mapping
   */
  static getStatusToSectionMap() {
    const language = Framework.getStoreRegistry().getStore('language');

    return {
      needreview: language.getMessage('aet.home.segmentNeedsReviewLabel'),
      needsreview: language.getMessage('aet.home.segmentNeedsReviewLabel'),
      gettingthere: language.getMessage('aet.home.segmentOkayLabel'),
      great: language.getMessage('aet.home.segmentGreatLabel'),
      okay: language.getMessage('aet.home.segmentOkayLabel'),
      na: 'N/A'
    };
  }

  /**
   * Converts a given status to a normalized label by lowercasing and removing all whitespace.
   * @param {string} status - The status value to normalize.
   * @returns {string} Normalized status label.
   */
  static normalizeStatus(status) {
    return status?.toLowerCase().replace(/\s/g, '') || '';
  }

  /**
   * Get section name from status
   * @param {string} status - The status value
   * @param {string} defaultSection - Default section name if status not found
   * @returns {string} Section name
   */
  static getSectionNameFromStatus(status, defaultSection = constants.PERFORMANCE_STATUS.NA) {
    if (!status) {
      return defaultSection;
    }

    const renderStatus = this.normalizeStatus(status);
    const statusMap = this.getStatusToSectionMap();
    return statusMap[renderStatus] || defaultSection;
  }

  /**
   * Remove a question from a section and return the moved question data
   * @param {Object} section - The section to remove from
   * @param {string} topic - The topic name
   * @param {string} questionId - The question ID to remove
   * @param {string} newStatus - The new status for the question
   * @returns {Object} Object containing movedQuestion, movedAssignment, and topicMetadata
   */
  static removeQuestionFromSection(section, topic, questionId, newStatus) {
    let movedQuestion = null;
    let movedAssignment = null;
    let topicMetadata = null;

    if (section.assignments?.[topic]) {
      section.assignments[topic].forEach((assignment, aIdx) => {
        const qIdx = assignment.questions.findIndex(q => q.questionId === questionId);
        if (qIdx !== -1) {
          movedQuestion = assignment.questions[qIdx];
          movedQuestion.status = newStatus;
          assignment.questions.splice(qIdx, 1);

          if (assignment.questions.length === 0) {
            section.assignments[topic].splice(aIdx, 1);
          }

          movedAssignment = { ...assignment, questions: [movedQuestion] };
        }
      });

      topicMetadata = section.topics?.find(t => (typeof t === 'string' ? t : t?.name) === topic);

      if (section.assignments[topic].length === 0) {
        delete section.assignments[topic];
        section.topics = section.topics.filter(t => (typeof t === 'string' ? t : t?.name) !== topic);
      }

      section.count = Math.max(0, (section.count || 0) - 1);
    }

    return { movedQuestion, movedAssignment, topicMetadata };
  }

  /**
   * Add a question to a section
   * @param {Object} section - The section to add to
   * @param {string} topic - The topic name
   * @param {Object} movedQuestion - The question to add
   * @param {Object} movedAssignment - The assignment containing the question
   * @param {Object} topicMetadata - Metadata for the topic (video, practice)
   */
  static addQuestionToSection(section, topic, movedQuestion, movedAssignment, topicMetadata) {
    if (!movedQuestion || !movedAssignment) {
      return;
    }

    // Ensure assignments object exists
    if (!section.assignments) {
      section.assignments = {};
    }

    // Ensure topic exists in assignments
    if (!section.assignments[topic]) {
      section.assignments[topic] = [];
    }

    // Add or merge assignment
    const existingAssignmentIdx = section.assignments[topic].findIndex(
      a => a.assignmentId === movedAssignment.assignmentId
    );

    if (existingAssignmentIdx !== -1) {
      section.assignments[topic][existingAssignmentIdx].questions.push(movedQuestion);
    } else {
      section.assignments[topic].push(movedAssignment);
    }

    // Ensure topic exists in topics array with video and practice metadata
    const topicExists = section.topics?.some(t => (typeof t === 'string' ? t : t?.name) === topic);

    if (!topicExists) {
      if (!section.topics) {
        section.topics = [];
      }
      if (topicMetadata) {
        section.topics.push(topicMetadata);
      } else {
        section.topics.push({ name: topic, video: null, practice: null });
      }
    } else {
      const existingTopicIdx = section.topics.findIndex(t => (typeof t === 'string' ? t : t?.name) === topic);
      if (existingTopicIdx !== -1 && topicMetadata) {
        const existingTopic = section.topics[existingTopicIdx];
        if (typeof existingTopic === 'string') {
          section.topics[existingTopicIdx] = topicMetadata;
        } else {
          if (!existingTopic.video && topicMetadata.video) {
            existingTopic.video = topicMetadata.video;
          }
          if (!existingTopic.practice && topicMetadata.practice) {
            existingTopic.practice = topicMetadata.practice;
          }
        }
      }
    }

    section.count = (section.count || 0) + 1;
  }

  /**
   * Move a question from one section to another based on status change
   * @param {Array} reviewData - The review data array containing sections
   * @param {Object} changeInfo - Object containing questionId, topic, oldStatus, newStatus
   * @returns {Array} Updated review data
   */
  static moveQuestionBetweenSections(reviewData, changeInfo) {
    const {
      questionId, topic, oldStatus, newStatus
    } = changeInfo;

    // If status hasn't changed, return early
    if (oldStatus === newStatus) {
      return reviewData;
    }

    const language = Framework.getStoreRegistry().getStore('language');
    const defaultSection = language.getMessage('aet.home.segmentNeedsReviewLabel');

    // Deep clone the data to avoid mutations
    const newData = JSON.parse(JSON.stringify(reviewData));

    const oldSectionName = this.getSectionNameFromStatus(oldStatus, defaultSection);
    const newSectionName = this.getSectionNameFromStatus(newStatus, defaultSection);

    const oldSection = newData.find(s => s.name === oldSectionName);
    const newSection = newData.find(s => s.name === newSectionName);

    if (!oldSection || !newSection) {
      console.warn('Section not found:', { oldSectionName, newSectionName });
      return newData;
    }

    // Remove question from old section
    const { movedQuestion, movedAssignment, topicMetadata } = this.removeQuestionFromSection(
      oldSection,
      topic,
      questionId,
      newStatus
    );

    // Add question to new section
    this.addQuestionToSection(newSection, topic, movedQuestion, movedAssignment, topicMetadata);

    return newData;
  }

  // eslint-disable-next-line class-methods-use-this
  static getMcqData = (data) => {
    if (!data) {
      return;
    }

    const optionList = data?.options?.map(ele => ({
      id: ele?.id,
      label: ele?.distractor,
      value: ele?.id,
      correct: ele?.correctChoice === constants.YES.charAt(0).toUpperCase() + constants.YES.slice(1),
      feedback: ele.feedback
    }));

    const selectedOptionsList = data?.answerAttempts?.map(ele => ({
      id: optionList.find(option => option.id === ele.answerNumber)?.id,
      label: optionList.find(option => option.id === ele.answerNumber)?.label,
      value: optionList.find(option => option.id === ele.answerNumber)?.value,
      correct: optionList.find(option => option.id === ele.answerNumber)?.correct,
      feedback: optionList.find(option => option.id === ele.answerNumber)?.feedback
    }));

    const mcqData = {
      questionType: data?.questionType,
      pageId: data?.pageId,
      questionId: data?.questionId,
      chapterTitle: data?.topic,
      attempts: data?.maxAttempts,
      questionStem: data?.questionStem,
      options: optionList || [],
      selectedOptions: selectedOptionsList || [],
      status: data?.answerConfidence?.toLowerCase().replace(/\s/g, ''),
      isShowAttempt: true,
      topic: data?.topic
    };

    // eslint-disable-next-line consistent-return
    return mcqData;
  };

  static getProgressCardColor = (type) => {
    const colorPalette = {
      completed: statusColorUtils.getProgressColor(constants.STATUS_COLOR_MAP.COMPLETED),
      completedPastDue: statusColorUtils.getProgressColor(constants.STATUS_COLOR_MAP.COMPLETED_PAST_DUE),
      inprogress: statusColorUtils.getProgressColor(constants.STATUS_COLOR_MAP.IN_PROGRESS),
      notstarted: statusColorUtils.getProgressColor(constants.STATUS_COLOR_MAP.NOT_STARTED),
      great: statusColorUtils.getProgressColor(constants.PERFORMANCE_STATUS.GREAT),
      gettingthere: statusColorUtils.getProgressColor(constants.PERFORMANCE_STATUS.GETTING_THERE),
      needsreview: statusColorUtils.getProgressColor(constants.PERFORMANCE_STATUS.NEEDS_REVIEW),
      'N/A': statusColorUtils.getProgressColor(constants.PERFORMANCE_STATUS.NA)
    };

    return colorPalette[type];
  };

  static getBadgeTooltipColor = (type) => {
    const colorPalette = {
      completedOnTime: statusColorUtils.getBadgeTooltipColor(constants.BADGE_TOOLTIP_STATUS.COMPLETED_ON_TIME),
      completedPastDue: statusColorUtils.getBadgeTooltipColor(constants.BADGE_TOOLTIP_STATUS.COMPLETED_PAST_DUE)
    };

    return colorPalette[type];
  };


  /**
   * Method to get chapters for channel remediation
   * @param {Array} assignments
   * @returns {Array} - Array of chapters
   */
  static getChaptersForChannelRemediation = (assignments) => {
    if (!assignments || !Array.isArray(assignments)) {
      return null;
    }

    // key: chapterTitle, value: object with chapterName, subChapterName, topics, hasTopics
    const chapterMap = new Map();
    assignments.forEach((assignment) => {
      const { mcqs } = assignment || {};
      if (!mcqs || !Array.isArray(mcqs)) {
        return;
      }

      mcqs.forEach((mcq) => {
        const { chapterTitle } = mcq || {};
        if (!chapterTitle) {
          return;
        }

        if (!chapterMap.has(chapterTitle)) {
          chapterMap.set(chapterTitle, {
            chapterName: chapterTitle,
            subChapterName: chapterTitle,
            topics: [mcq.topic],
            hasTopics: true
          });
        } else {
          chapterMap.get(chapterTitle).topics.push(mcq.topic);
        }
      });
    });

    return Array.from(chapterMap.values());
  };

  /**
     * Method show syllabus actions
     * @returns {boolean} - true/false
     */
  static showSyllabusActions = (isSaveAndAddClicked) => {
    const assignmentStore = Framework.getStoreRegistry().getStore('assignment');

    return !isSaveAndAddClicked && assignmentStore?.assignments?.length > 0;
  };

  /**
   * True when assignment is published (ASSIGNED). Upnext/L2 views exclude ASSIGNABLE drafts.
   * @param {Object} assignment
   * @returns {boolean}
   */
  static isAssignedAssignment(assignment) {
    return assignment?.assignmentStatus === ASSIGNMENT_STATUS_TYPES.ASSIGNED;
  }

  /**
   * Publish UI flags for copied ASSIGNABLE courses (assignment store table).
   * @param {Array} assignments - Assignment store rows
   * @returns {{ showPublishButton: boolean, showFooter: boolean }}
   */
  static getCopiedCoursePublishUI = (assignments = []) => ({
    showPublishButton: assignments.some(
      (assignment) => assignment.assignmentStatus === ASSIGNMENT_STATUS_TYPES.ASSIGNABLE
    ),
    showFooter: true
  });

  /**
     * Method show syllabus publish button
     * @param {boolean} isUploadSuccess
     * @returns {boolean} - true/false
     */
  static showSyllabusPublishButton = (isUploadSuccess) => {
    if (!isUploadSuccess) {
      return false;
    }

    const assignmentStore = Framework.getStoreRegistry().getStore('assignment');
    const syllabusStore = Framework.getStoreRegistry().getStore('syllabus');
    const storeAssignments = assignmentStore?.assignments || [];
    const hasAssignableRows = storeAssignments.some(
      (row) => row.assignmentStatus === ASSIGNMENT_STATUS_TYPES.ASSIGNABLE
    );
    const syllabusSchedule = getSnapshot(syllabusStore)?.syllabusInfo?.data?.schedule || [];

    return syllabusSchedule.length > 0 || hasAssignableRows;
  };

  /**
     * Method show dynamic toggle and footer
     * @param {boolean} isUploadSuccess
     * @returns {boolean} - true/false
     */
  static showDynamicToggleAndFooter = (isUploadSuccess, isSaveAndAddClicked) => {
    const assignmentStore = Framework.getStoreRegistry().getStore('assignment');

    if (!isUploadSuccess && isSaveAndAddClicked) {
      return false;
    }

    return isUploadSuccess || assignmentStore?.assignments?.length > 0;
  };

  /**
     * Method to show syllabus upload tabs
     * @param {boolean} isUploadSuccess
     * @returns {boolean} - true/false
     */
  static showSyllabusUploadTabs = (isUploadSuccess) => {
    const assignmentStore = Framework.getStoreRegistry().getStore('assignment');
    const syllabusStore = Framework.getStoreRegistry().getStore('syllabus');
    const assignmentsLength = getSnapshot(syllabusStore)?.syllabusInfo?.assignments?.length || 0;

    return isUploadSuccess || !assignmentsLength > 0 || !assignmentStore?.assignments?.length > 0;
  };

  /**
   * Method to convert percentage to count
   * @param {number} percentage - The percentage to convert
   * @param {number} total - The total to convert to
   * @returns {number} - The count
   */
  static convertPercentageToCount = (percentage, total) => Math.round((Number(percentage) / 100) * Number(total));

  /** Per-student due date: an override always wins over the assignment default, even if later. */
  static resolveEffectiveDueDate = ({
    studentsRoster,
    studentId,
    assignmentId,
    fallbackDueDate = null
  } = {}) => {
    const overrideDueDate = (studentsRoster && studentId && assignmentId)
      ? studentsRoster.getStudentSpecificDueDatesRecord?.(studentId)?.[assignmentId]
      : null;

    return overrideDueDate ?? fallbackDueDate ?? null;
  };

  /**
   * Instructor-facing student progress badge label.
   * Not started: label only. Other statuses: label + "{count} of {total}".
   * @param {Object} data - Progress fields (questionsAttempted, status, etc.)
   * @returns {{ label: string, progressStatus: string }}
   */
  static getInstructorStudentProgress = (data = {}) => {
    const language = Framework.getStoreRegistry().getStore('language');
    const attempted = data.questionsAttempted ?? data.attemptedQuestions ?? data.completedQuestions ?? 0;
    const total = data.questionsAssigned ?? data.totalQuestions ?? 0;
    const isPastDue = hasAssignmentDueDatePassed(data.dueDate);

    let labelKey = 'aet.home.progressNotStartedLabel';
    let progressStatus = constants.PROGRESS_STATUS.NOT_STARTED;
    const status = data.questionsAttemptedLate > 0
      ? constants.PROGRESS_STATUS_IDS.COMPLETED_LATE
      : (data.status ?? data.studentProgress);

    if (
      CommonUtils.localStringCompare(status, constants.PROGRESS_STATUS_IDS.COMPLETED)
      || CommonUtils.localStringCompare(status, constants.STATUS_COLOR_MAP.COMPLETED)
    ) {
      labelKey = 'aet.home.progressCompletedOnTimeLabel';
      progressStatus = constants.PROGRESS_STATUS.COMPLETED;
    } else if (
      CommonUtils.localStringCompare(status, constants.PROGRESS_STATUS_IDS.COMPLETED_LATE)
      || data.questionsAttemptedLate > 0
    ) {
      labelKey = 'aet.home.progressCompletedPastDueLabel';
      progressStatus = constants.PROGRESS_STATUS.COMPLETED_LATE;
    } else if (
      CommonUtils.localStringCompare(status, constants.PROGRESS_STATUS_IDS.IN_PROGRESS)
      || CommonUtils.localStringCompare(status, constants.STATUS_COLOR_MAP.IN_PROGRESS)
    ) {
      labelKey = isPastDue ? 'aet.home.progressPartiallyCompleteLabel' : 'aet.home.progressInProgressLabel';
      progressStatus = constants.PROGRESS_STATUS.IN_PROGRESS;
    }

    const showCount = progressStatus !== constants.PROGRESS_STATUS.NOT_STARTED;
    let countSuffix = '';
    if (showCount) {
      countSuffix = language
        .getMessage('aet.home.progressCountOfTotal')
        .replace('{count}', attempted)
        .replace('{total}', total);
    }

    const statusLabel = language.getMessage(labelKey);

    return {
      label: countSuffix ? `${statusLabel} ${countSuffix}` : statusLabel,
      progressStatus
    };
  };

  /**
   * Method to get the student progress card data
   * @param {Array} progress - The progress to convert
   * @param {number} totalAssignments - The total assignments to convert to
   * @returns {Array} - The student progress card data
   */
  static getStudentProgressCardData = (progress, totalAssignments = 0) => {
    if (!progress) {
      return [];
    }

    return progress.map(item => ({
      ...item,
      value: AssignmentHelperUtils.convertPercentageToCount(item.value, totalAssignments),
      valueInPercentage: item.value
    }));
  };

  /** This method used to check for duplicate titles when creating a new assignment. */
  static combineExistingAssignmentTitles = (syllabus, assignment) => {
    const syllabusTitles = syllabus?.excludeTitles || [];
    const assignmentTitles = assignment?.assignments?.map(a => a.title) || [];
    const localAssignmentTitles = syllabus?.syllabusInfo?.assignments?.map(a => a.title) || [];
    const scheduleTitles = syllabus?.syllabusInfo?.data?.schedule?.map(a => a.title) || [];
    return Array.from(new Set([
      ...syllabusTitles,
      ...assignmentTitles,
      ...localAssignmentTitles,
      ...scheduleTitles
    ].filter(Boolean)));
  };

  /**
 * Generates initials from a full name.
 *
 * @param {string} name - The full name of the person (e.g., "Liam Chen").
 * @returns {string} - Returns the initials in uppercase (e.g., "LC").
 *                     If the name is empty or invalid, returns an empty string.
 */
  static getInitials = (name = '') => {
    if (!name) return '';

    const [first = '', last = ''] = name.trim().split(/\s+/);
    return `${first[0] || ''}${last[0] || ''}`.toUpperCase();
  };


  /** Numeric risk for one badge row (`def.riskKey` on insight risk badge defs). */
  static insightRiskValueForRow(row, def) {
    return Number(row[def.riskKey] ?? 0);
  }

  /**
   * Behavior trend value text for question insights (e.g. `"1 of 1 students"`).
   * @param {number} count
   * @param {number} total
   * @param {Object} language - Language store.
   * @returns {string}
   */
  static formatInsightCountOfStudents(count, total, language) {
    return language
      .getMessage('aet.home.insightRisk.countOfStudents')
      .replace('{count}', String(count))
      .replace('{total}', String(total));
  }

  /** Sort badge rows by `badgeDefs` order (not by score). */
  static sortInsightRiskBadgesByDefinition(results, badgeDefs) {
    const order = badgeDefs.map(def => def.riskKey);
    return [...results].sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
  }

  /**
   * Badges for one API row (question or student): include only if value is greater than threshold.
   * Order follows `badgeDefs` (see INSIGHT_RISK_BADGES_QUESTION / INSIGHT_RISK_BADGES_STUDENT).
   * @param {number} [threshold] Defaults to INSIGHT_RISK_BADGE_DISPLAY_THRESHOLD (explicit `undefined` uses default too).
   * @param {number|null} [maxBadges=null] If set, return at most this many badges (after sort).
   * @param {boolean|{ showAll?: boolean, badgeDefs?: Array, removeLowEngagementWhenMultiple?: boolean }} [options=false] `true` or `{ showAll: true }` skips threshold filter.
   */
  static getInsightRiskBadgeResults(row, threshold, maxBadges = null, options = false) {
    if (!row || !CommonUtils.isPlainObject(row)) {
      return [];
    }

    const opts = typeof options === 'boolean' ? { showAll: options } : options;
    const { showAll = false, badgeDefs = INSIGHT_RISK_BADGES_QUESTION, removeLowEngagementWhenMultiple = false } = opts;

    let results = badgeDefs.map(def => ({
      key: def.riskKey,
      labelKey: def.labelKey,
      value: AssignmentHelperUtils.insightRiskValueForRow(row, def)
    }));

    if (!showAll) {
      const th = threshold ?? AssignmentHelperUtils.getAetInsightsThresholds().insightRiskBadgeDisplayThreshold;
      results = results.filter(x => x.value > th);
    }

    results = AssignmentHelperUtils.sortInsightRiskBadgesByDefinition(results, badgeDefs);

    if (maxBadges != null && Number.isFinite(maxBadges) && maxBadges >= 0) {
      results = results.slice(0, maxBadges);
    }

    if (removeLowEngagementWhenMultiple && results.length > 1) {
      results = results.filter(b => b.key !== ENGAGEMENT_INSIGHT_RISK_KEY);
    }

    return results;
  }

  /**
   * Question statistics (store/API) → fields consumed by the Report question modal.
   * @param {*} statisticsData - e.g. mcqAssignments.getStatisticsData(questionId)
   * @returns {Object}
   */
  static getReportModalFieldsFromStatistics(statisticsData) {
    const reportModalFields = {};
    if (!CommonUtils.isPlainObject(statisticsData)) {
      return reportModalFields;
    }
    const { totalStudentReports, reportBreakdown, studentComments } = statisticsData;
    if (totalStudentReports != null && totalStudentReports !== '') {
      reportModalFields.totalStudentReports = totalStudentReports;
    }
    if (CommonUtils.isPlainObject(reportBreakdown) && !CommonUtils.isEmpty(reportBreakdown)) {
      reportModalFields.reportBreakdown = reportBreakdown;
    }
    if (Array.isArray(studentComments) && studentComments.length > 0) {
      reportModalFields.studentComments = studentComments;
    }
    return reportModalFields;
  }

  /**
   * Helper to build difficultTopicReviewData for DifficultTopicDetails / ReportQuestionModal.
   * @param {string} questionId
   * @param {Object} [reviewModalInput]
   * @returns {Object}
   */
  static buildDifficultTopicReviewData(questionId, reviewModalInput = {}) {
    const result = {
      question: {
        id: questionId,
        ...(CommonUtils.isPlainObject(reviewModalInput.question) ? reviewModalInput.question : {})
      }
    };
    if (reviewModalInput.totalStudentReports != null && reviewModalInput.totalStudentReports !== '') {
      result.totalStudentReports = Number(reviewModalInput.totalStudentReports);
    }
    if (
      CommonUtils.isPlainObject(reviewModalInput.reportBreakdown)
      && !CommonUtils.isEmpty(reviewModalInput.reportBreakdown)
    ) {
      result.reportBreakdown = reviewModalInput.reportBreakdown;
    }
    if (Array.isArray(reviewModalInput.studentComments) && reviewModalInput.studentComments.length > 0) {
      result.studentComments = reviewModalInput.studentComments;
    }
    if (reviewModalInput.bookId != null && reviewModalInput.bookId !== '') {
      result.bookId = reviewModalInput.bookId;
    }
    if (CommonUtils.isPlainObject(reviewModalInput.feedbackSubmit)) {
      result.feedbackSubmit = reviewModalInput.feedbackSubmit;
    }
    return result;
  }

  /**
   * Statistics snapshot → difficultTopicReviewData and numeric reviewCount for the header badge.
   * @param {string} questionId
   * @param {*} statisticsData
   * @param {string} [bookId] - current hero book id for report/feedback context
   * @returns {{ difficultTopicReviewData: Object, reviewCount: number }}
   */
  static createDifficultTopicReviewData(questionId, statisticsData, bookId = null) {
    const reportModalFields = AssignmentHelperUtils.getReportModalFieldsFromStatistics(statisticsData);
    const reviewInput = { ...reportModalFields };
    if (bookId) {
      reviewInput.bookId = bookId;
    }
    // TODO: bring from constants
    reviewInput.feedbackSubmit = {
      type: 'aet_mcqs',
      feedback: 'DISLIKE',
      context: bookId ? [{ key: 'bookId', value: bookId }] : []
    };
    const difficultTopicReviewData = AssignmentHelperUtils.buildDifficultTopicReviewData(
      questionId,
      reviewInput
    );
    const reviewCount = typeof difficultTopicReviewData.totalStudentReports === 'number'
      ? difficultTopicReviewData.totalStudentReports
      : 0;
    return { difficultTopicReviewData, reviewCount };
  }

  static addAssignmentQuestionNumbersToDifficultQuestions(assignment, difficultQuestions) {
    const rows = difficultQuestions || [];
    if (!rows.length) return [];

    const indexById = new Map();
    let n = 0;
    (assignment?.contentCovered || []).forEach((block) => {
      (block?.questions || []).forEach((q) => {
        const id = q?.questionId;
        if (id != null && !indexById.has(id)) {
          n += 1;
          indexById.set(id, n);
        }
      });
    });
    return rows.map(row => ({
      ...row,
      assignmentQuestionNumber: indexById.get(row.questionId) ?? null
    }))
      .sort((a, b) => (a.assignmentQuestionNumber ?? Infinity) - (b.assignmentQuestionNumber ?? Infinity));
  }

  /**
   * Stem prefixed as "Q n: …" when `assignmentQuestionNumber` is a finite index ≥ 1; otherwise plain `stem`.
   */
  static difficultQuestionDisplayStem(stem = '', assignmentQuestionNumber) {
    const n = Number(assignmentQuestionNumber);
    return Number.isFinite(n) && n >= 1 ? `Q${n}: ${stem}` : stem;
  }

  static setAetInsightsThresholdResolver(fn) {
    aetInsightsThresholdResolver = typeof fn === 'function' ? fn : null;
  }

  /** Merges heroView template values from the registered resolver with code fallbacks. */
  static getAetInsightsThresholds() {
    if (aetInsightsThresholdResolver) {
      try {
        const resolved = aetInsightsThresholdResolver();
        if (resolved && typeof resolved === 'object') {
          return { ...fallbackAetInsightsThresholds(), ...resolved };
        }
      } catch (e) {
        /* Marquee not ready */
      }
    }
    return fallbackAetInsightsThresholds();
  }

  /** Difficult-questions list filtered by `performanceRisk` (inclusive minimum). Used for Up Next and toggle-on only. */
  static filterDifficultQuestionsByOverallRisk(
    questions,
    minRisk = AssignmentHelperUtils.getAetInsightsThresholds().overallDifficultyRiskMinDisplay
  ) {
    return (questions || []).filter(question => question.performanceRisk >= minRisk);
  }

  /** Struggling students: `overallRisk` descending, then name. */
  static sortStruggleStudentsByOverallDifficultyDescending(students) {
    return [...(students || [])].sort((a, b) => {
      const byRisk = b.overallRisk - a.overallRisk;
      return byRisk || (a.name || '').localeCompare(b.name || '');
    });
  }

  static filterStruggleStudentsByOverallRisk(
    students,
    minRisk = AssignmentHelperUtils.getAetInsightsThresholds().overallStudentRiskMinDisplay
  ) {
    return (students || []).filter(row => row.performanceRisk >= minRisk);
  }

  /**
   * Whether studentId is in the assignment's struggling-students list (overall risk threshold).
   * Used by analytics events 922 and 966.
   *
   * @param {Object} assignment - Assignment store with getStruggleStudentsWithNames
   * @param {string|number} assignmentId
   * @param {string|number} studentId
   * @returns {boolean}
   */
  static isStrugglingStudentForAssignment(assignment, assignmentId, studentId) {
    const struggleRowsRaw = assignment?.getStruggleStudentsWithNames?.(assignmentId) || [];
    const strugglingByRisk = AssignmentHelperUtils.filterStruggleStudentsByOverallRisk(struggleRowsRaw);
    return strugglingByRisk.some((s) => String(s.userId) === String(studentId));
  }

  /**
   * Custom fields for Open Specific Student Insights and
   * View Specific Assignment Student Insights.
   *
   * @eventNumber 922
   * @eventNumber 966
   * @param {Object} context - Plugin context (for aetCustomFields)
   * @param {Object} options
   * @param {string} options.locationInApp
   * @param {string|number} options.assignmentId
   * @param {string|number} options.studentId
   * @param {Object} options.assignment
   * @param {string} [options.initiatePoint]
   * @param {boolean} [options.includeStudentId=true]
   * @returns {Object}
   */
  static buildSpecificAssignmentStudentInsightsCustomFields(
    context,
    {
      locationInApp,
      assignmentId,
      studentId,
      assignment,
      initiatePoint = null,
      includeStudentId = true
    }
  ) {
    const isStrugglingStudent = AssignmentHelperUtils.isStrugglingStudentForAssignment(
      assignment,
      assignmentId,
      studentId
    );

    const customFields = {
      ...CommonUtils.aetCustomFields(context, locationInApp),
      assignment_id: String(assignmentId),
      is_struggling_student: isStrugglingStudent
    };

    if (includeStudentId && studentId != null) {
      customFields.student_id = studentId;
    }
    if (initiatePoint != null) {
      customFields.initiate_point = initiatePoint;
    }

    return customFields;
  }
}


export default AssignmentHelperUtils;
